import { useEffect, useRef, useCallback } from 'react';

interface UseInfiniteScrollOptions {
  onLoadMore: () => void;
  hasMore: boolean;
  loading: boolean;
  threshold?: number;
  rootMargin?: string;
  /**
   * The scroll container the sentinel scrolls within, when it isn't the page — so `rootMargin`
   * reaches ahead inside that container and the next page loads before its end comes into view.
   * Default: the viewport.
   */
  root?: Element | null;
}

interface UseInfiniteScrollReturn {
  sentinelRef: React.RefObject<HTMLDivElement>;
}

/**
 * Calls `onLoadMore` when the returned sentinel scrolls into view (within `rootMargin` of the
 * `root`, or of the viewport), while there is more to load and nothing is loading.
 *
 * @param {UseInfiniteScrollOptions} options - The load callback, its guards, and the observer's
 *   threshold, root margin and root.
 * @return {UseInfiniteScrollReturn} The ref to attach to the sentinel element.
 */
export function useInfiniteScroll({
  onLoadMore,
  hasMore,
  loading,
  threshold = 0.1,
  rootMargin = '100px',
  root,
}: UseInfiniteScrollOptions): UseInfiniteScrollReturn {
  const sentinelRef = useRef<HTMLDivElement>(null);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loading) {
        onLoadMore();
      }
    },
    [onLoadMore, hasMore, loading]
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(handleIntersection, {
      threshold,
      rootMargin,
      root,
    });

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [handleIntersection, threshold, rootMargin, root]);

  return { sentinelRef };
}
